import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { vendorApi } from "../../../api/api";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import GradientButton from "../../common/GradiantButton";

const documentTypes = [
  { label: "EC", value: "EC" },
  { label: "Patta", value: "Patta" },
  { label: "Other", value: "Other" },
];

const YourApartmentsPlotsAddDocuments = () => {
  const [docType, setDocType] = useState("");
  const [docName, setDocName] = useState("");
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const theme = useSelector((data) => data.themeValue.colors);
  const { state } = useLocation();
  const apartmentData = state?.property || {};
  const apartmentId = apartmentData?.id;

  const onDrop = useCallback((acceptedFiles) => {
    const pdf = acceptedFiles.find((file) => file.type === "application/pdf");
    if (pdf) setFile(pdf);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
  });

  const handleUpload = async () => {
    if (!docType || !docName || !file) {
      setSnackbar({
        open: true,
        message: "Please fill all fields and upload a PDF file",
        severity: "error",
      });
      return;
    }

    if (!apartmentId) {
      setSnackbar({
        open: true,
        message: "Apartment ID is missing.",
        severity: "error",
      });
      return;
    }

    const formData = new FormData();
    formData.append("apartmentId", apartmentId);

    const docPayload = {
      docType: docType,
      docOrder: 1,
      isAdditionalDocument: false,
      MarketUpId: null,
      docName: docName,
      type: "Apartment", // Important to specify apartment
    };

    formData.append("Documents[]", JSON.stringify(docPayload));
    formData.append("file[0]", file);

    try {
      
      setIsLoading(true);
      const response = await vendorApi.addApartmentDocuments(formData);

      if (response.status === 200 || response.status === 201) {
        setSnackbar({
          open: true,
          message: "Document uploaded successfully!",
          severity: "success",
        });
        setDocType("");
        setDocName("");
        setFile(null);
      } else {
        throw new Error(response?.data?.message || "Unexpected error");
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.error || error.message || "Upload failed",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 500, margin: "auto", mt: 4 }}>
      <Typography variant="h6" gutterBottom sx={{ color: theme.text }}>
        Add Apartment Document
      </Typography>

      <Stack spacing={2}>
        <TextField
          label="Document Type"
          select
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          fullWidth
          sx={{
            '& .MuiInputLabel-root': {
              color: theme.text,
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: theme.primary_color,
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.text,
              },
              '&:hover fieldset': {
                borderColor: theme.primary_color,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.primary_color,
              },
            },
            // 👇 This is the selected value text color
            '& .MuiSelect-select': {
              color: theme.text,
            },
          }}
        >
          {documentTypes.map((option) => (
            <MenuItem key={option.value} value={option.value} >
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Document Name"
          value={docName}
          onChange={(e) => setDocName(e.target.value)}
          fullWidth
          sx={{
            // mb: 3,
            input: {
              color: theme.text,
            },
            label: {
              color: theme.text,
            },
            '& label.Mui-focused': {
              color: theme.primary_color,
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.text,
              },
              '&:hover fieldset': {
                borderColor: theme.primary_color,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.primary_color,
              },
            },
          }}
        />

        <Paper
          {...getRootProps()}
          sx={{
            p: 3,
            border: "2px dashed #90caf9",
            backgroundColor: isDragActive ? "#f0f8ff" : "#fafafa",
            cursor: "pointer",
            textAlign: "center",
          }}
        >
          <input {...getInputProps()} />
          <Typography>
            {isDragActive
              ? "Drop the PDF file here..."
              : "Drag & drop PDF here, or click to select"}
          </Typography>
          <Button variant="outlined" sx={{ mt: 1 }}>
            Select File
          </Button>
        </Paper>

        {file && (
          <List>
            <ListItem>
              <ListItemIcon>
                <PictureAsPdfIcon color="error" />
              </ListItemIcon>
              <ListItemText primary={file.name} />
            </ListItem>
          </List>
        )}
        <GradientButton 
        text={isLoading ? <CircularProgress size={24} color="inherit" /> : "Upload Document"}
        onClick={handleUpload}
        width="100%"/>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default YourApartmentsPlotsAddDocuments;